import { useState } from "react";
import { useStore } from "../store/store";
import { useT } from "../i18n/useT";
import { validateFlow } from "@mensajeando/flows";

export function Flows() {
  const t = useT();
  const flows = useStore((s) => s.flows);
  const list = Object.values(flows);
  const [selectedId, setSelectedId] = useState(list[0]?.id);
  const flow = selectedId ? flows[selectedId] : undefined;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">{t.nav.flows}</h1>
        <p className="text-sm text-slate-500">Bilingual branching survey graphs. Each node carries en + es content.</p>
      </header>
      <div className="grid grid-cols-[280px_1fr] gap-4">
        <div className="card overflow-hidden">
          <ul>
            {list.map((f) => (
              <li key={f.id}>
                <button
                  onClick={() => setSelectedId(f.id)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 ${selectedId === f.id ? "bg-brand-50" : ""}`}
                >
                  <div className="font-medium text-sm">{f.name}</div>
                  <div className="text-xs text-slate-500">v{f.version} · {Object.keys(f.nodes).length} nodes</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          {flow ? <FlowGraphView flow={flow} /> : <p className="text-sm text-slate-500">Select a flow.</p>}
        </div>
      </div>
    </div>
  );
}

function FlowGraphView({ flow }: { flow: ReturnType<typeof useStore.getState>["flows"][string] }) {
  const errors = validateFlow(flow);
  return (
    <div className="card card-pad">
      <h2 className="font-semibold">{flow.name}</h2>
      <div className="text-xs text-slate-500 mb-3">Entry: <code>{flow.entry}</code></div>
      {errors.length > 0 && (
        <div className="badge-err mb-3">{errors.length} validation issue(s)</div>
      )}
      <ol className="space-y-3">
        {Object.values(flow.nodes).map((n) => (
          <li key={n.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="badge-info">{n.kind}</span>
              <span className="font-medium">{n.label}</span>
              <span className="text-slate-400 text-xs ml-auto">{n.id}</span>
            </div>
            <NodeBody node={n} />
          </li>
        ))}
      </ol>
    </div>
  );
}

function NodeBody({ node }: { node: any }) {
  switch (node.kind) {
    case "message":
      return (
        <div className="text-sm space-y-1">
          <Bi en={node.body.en} es={node.body.es} />
          <div className="text-xs text-slate-500">→ {node.next}</div>
        </div>
      );
    case "question":
      return (
        <div className="text-sm space-y-1">
          <Bi en={node.prompt.en} es={node.prompt.es} />
          <div className="text-xs text-slate-500">
            Stores under <code>{node.answerKey}</code>. Choices: {node.choices.map((c: any) => c.id).join(", ")}. → {node.next} (fallback: {node.fallback ?? "—"})
          </div>
        </div>
      );
    case "branch":
      return (
        <div className="text-sm space-y-1">
          {node.rules.map((r: any, i: number) => (
            <div key={i} className="text-xs">
              if <code>{r.when.answerKey}</code> = {JSON.stringify(r.when.equals ?? r.when.in)} → <code>{r.goto}</code>
            </div>
          ))}
          <div className="text-xs text-slate-500">else → <code>{node.default}</code></div>
        </div>
      );
    case "tag":
      return <div className="text-xs text-slate-600">add: {node.add?.join(", ") || "—"} · remove: {node.remove?.join(", ") || "—"} → {node.next}</div>;
    case "unsubscribe":
      return <div className="text-xs text-rose-700">unsubscribe reason: {node.reason}</div>;
    case "agent_escalation":
      return <div className="text-xs text-amber-700">escalate to: {node.queue}</div>;
    case "completion":
      return <div className="text-xs text-emerald-700">end</div>;
    default:
      return null;
  }
}

function Bi({ en, es }: { en: string; es: string }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded bg-slate-50 px-2 py-1.5"><span className="text-[10px] uppercase text-slate-500">EN</span><div>{en}</div></div>
      <div className="rounded bg-slate-50 px-2 py-1.5"><span className="text-[10px] uppercase text-slate-500">ES</span><div>{es}</div></div>
    </div>
  );
}
